import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import z from "zod";

import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { DangerButton } from "components/atoms/DangerButton";
import { InfoColumn, InfoColumnLabel, InfoRow } from "components/atoms/InfoRow";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import TextField from "components/organisms/form/TextField";
import ToggleField from "components/organisms/form/ToggleField";
import { useGetFeatureFlagsQuery } from "pages/Features/Feature flags/FeatureFlags.graphql-gen";
import { AddButton, RemoveButton } from "pages/Features/Grupper/AddUserModal";
import { useDeleteGroupMutation, useGetGroupQuery, useUpsertGroupMutation } from "pages/Features/Grupper/Groups.graphql-gen";
import { HeaderModalButton } from "pages/Users/UsersPage";

export const GroupSchema = z.object({
	displayName: z.string().min(1),
	key: z.string().min(1),
	usersCanOptIn: z.boolean(),
	description: z.string(),
});

export default function EditGroupPage() {
	const { data: featureFlagsData } = useGetFeatureFlagsQuery();
	const { groupId: rawGroupId } = useParams<{ groupId: string }>();
	const groupId = rawGroupId === "0" ? undefined : rawGroupId;

	const navigate = useNavigate();
	const [upsertGroup, { loading: upsertLoading, error }] = useUpsertGroupMutation();
	const { data, loading } = useGetGroupQuery({ variables: { groupId: groupId ?? "" }, skip: !groupId });
	const [deleteGroup, { loading: deleteLoading }] = useDeleteGroupMutation();
	const [flags, setFlags] = useState<string[]>([]);

	useEffect(() => {
		if (data) {
			setFlags(data.group?.featureFlagKeys ?? []);
		}
	}, [data]);

	const nonAddedNonGlobalFlags = useMemo(
		() => featureFlagsData?.featureFlags?.filter((f) => !flags?.includes(f.key))?.filter((f) => !f.global),
		[flags, featureFlagsData?.featureFlags]
	);

	if (groupId && !data?.group) {
		// Skjemaet får ikke med seg at data oppdateres uten denne
		return <>Laster..</>;
	}

	return (
		<Flex direction={Direction.column} gap="var(--grid-2)">
			<MainLayoutPageHeader
				loading={loading || deleteLoading}
				title={groupId ? `Rediger ${data?.group?.displayName ?? "Gruppe"}` : "Ny gruppe"}
				intro={groupId ? `${data?.group?.usersCount ?? 0} brukere i gruppen` : ""}
			/>
			<Form
				key={groupId}
				loading={loading || upsertLoading}
				value={{
					...data?.group,
				}}
				onSubmit={async (group) => {
					const { data: response, error } = await upsertGroup({
						variables: {
							group: {
								id: groupId,
								featureFlagKeys: flags,
								description: group.description,
								key: group.key,
								displayName: group.displayName,
							},
						},
					});

					!error && navigate("/groups/" + response?.upsertGroup?.id);
				}}
				schema={GroupSchema}
			>
				<Flex direction={Direction.column} gap="var(--grid-2)">
					<TextField label="Navn" name="displayName" placeholder="Ny gruppe" />
					<TextField label="Key" name="key" placeholder="nyGruppe" />
					<TextField label="Beskrivelse" name="description" placeholder="Hvorfor finnes denne gruppen?" />
					<ToggleField label="Brukere kan selv melde seg inn" name="usersCanOptIn" />
				</Flex>
				<Flex style={{ marginTop: "30px" }} direction={Direction.column} gap="var(--grid-2)">
					<h3>
						Feature flags{" "}
						<HeaderModalButton
							icon={<FontAwesomeIcon icon={faPlus} />}
							disabled={flags.length === featureFlagsData?.featureFlags?.filter((f) => !f.global).length}
						>
							<h3 style={{ textAlign: "center", marginTop: "20px" }}>Legg til flagg</h3>
							<AddFlagsModal
								currentFlags={flags}
								setFlags={setFlags}
								nonAddedNonGlobalFlags={nonAddedNonGlobalFlags?.map((f) => ({ key: f.key, displayName: f.displayName ?? "" }))}
							/>
						</HeaderModalButton>
					</h3>
					<p style={{ marginTop: "-10px", marginBottom: "-5px" }}>Merk: Kun ikke-globale feature flags kan legges til grupper.</p>

					{flags?.map((flag) => {
						const feature = featureFlagsData?.featureFlags?.find((f) => f.key === flag);
						return (
							<InfoRow key={flag} interactive>
								<InfoColumn width="90%">
									<InfoColumnLabel>{feature?.displayName}</InfoColumnLabel>
								</InfoColumn>
								<InfoColumn width="10%">
									<RemoveButton
										onClick={async () => {
											const filtered = flags.filter((f) => f !== flag);
											setFlags(filtered);
										}}
									>
										<FontAwesomeIcon icon={faMinus} />
									</RemoveButton>
								</InfoColumn>
							</InfoRow>
						);
					})}

					{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
				</Flex>

				<Flex style={{ marginTop: "50px" }} direction={Direction.column} gap="var(--grid-2)">
					<FormButton label={groupId ? "Oppdater gruppe" : "Opprett gruppe"} />
					{groupId && (
						<DangerButton
							label="Slett gruppe"
							onClick={async () => {
								if (!window.confirm(`Sikker på at du ønsker å slette gruppen?\nDen har ${data?.group?.usersCount} medlemmer`)) {
									return;
								}

								const { error } = await deleteGroup({
									variables: { groupId: groupId ?? "" },
								});

								if (error) {
									return console.error(error);
								}

								navigate("/groups/");
							}}
						/>
					)}
				</Flex>
			</Form>
		</Flex>
	);
}

interface AddFlagsModalProps {
	currentFlags: string[];
	nonAddedNonGlobalFlags?: {
		key: string;
		displayName: string;
	}[];
	setFlags: (flags: string[]) => void;
}

export function AddFlagsModal(props: Readonly<AddFlagsModalProps>) {
	return (
		<FindUserModalContentWrapper direction={Direction.column} gap="calc( var(--grid-2) * 2)" style={{ width: "calc(50vw)", height: "calc(50vh)" }}>
			<Flex direction={Direction.column} gap="var(--grid-2)">
				{props.nonAddedNonGlobalFlags?.map((flag) => {
					return (
						<InfoRow key={flag.key} interactive>
							<InfoColumn width="60%">
								<InfoColumnLabel>{flag?.displayName}</InfoColumnLabel>
							</InfoColumn>
							<InfoColumn>
								<AddButton
									onClick={async () => {
										const updated = [...props.currentFlags, flag.key];
										props.setFlags(updated);
									}}
								>
									<FontAwesomeIcon icon={faPlus} />
								</AddButton>
							</InfoColumn>
						</InfoRow>
					);
				})}
			</Flex>
		</FindUserModalContentWrapper>
	);
}

const FindUserModalContentWrapper = styled(Flex)`
	width: 50vw;
	height: 50vh;
	min-width: 300px;
	min-height: 300px;
	max-height: 100%;
	padding: 16px;
`;
